import { useState, useCallback, useEffect, useRef } from 'react';
import {
  ChatFeedbackModal,
  useFeedback,
  CHATAI_LOADING_STATUS,
  ChatAIInput,
  useChatContext,
} from '@ltvco/refresh-lib/v2';
import { useScreenSize } from '@ltvco/refresh-lib/utils';
import { MessagesContainer } from './RepairCostEstimatorContent/MessagesContainer';
import { CarRepairOutlined } from '@mui/icons-material';
import { theme } from 'theme';
import { useLocation } from 'react-router-dom';
import { FeatureHeader } from '../components/FeatureHeader';
import { FeaturePageShell } from '../components/FeaturePageShell';

export const RepairCostEstimatorPage = () => {
  const { isMobile } = useScreenSize();
  const {
    messages,
    loadingStatus,
    sendMessage,
    handleNewChat,
    handleFeatureNameChange,
  } = useChatContext();
  const [shouldResetInputs, setShouldResetInputs] = useState<boolean>(false);
  const location = useLocation();
  const prevLocationKeyRef = useRef<string | undefined>(location.key);

  const {
    openFeedback,
    thumbsUp,
    currentResponseId,
    feedback,
    createFeedback,
    updateFeedback,
    handleCloseFeedback,
  } = useFeedback(messages);

  const handleNewEstimate = useCallback((): void => {
    handleNewChat();
    setShouldResetInputs(true);
  }, [handleNewChat]);

  const handleResetComplete = useCallback((): void => {
    setShouldResetInputs(false);
  }, []);

  useEffect(() => {
    handleFeatureNameChange('createRepairCostEstimatorBot');
  }, [handleFeatureNameChange]);

  useEffect(() => {
    if (
      prevLocationKeyRef.current &&
      prevLocationKeyRef.current !== location.key
    ) {
      handleNewChat();
      setShouldResetInputs(true);
    }
    prevLocationKeyRef.current = location.key;
  }, [location.key, handleNewChat]);

  return (
    <FeaturePageShell
      header={
        <FeatureHeader
          icon={
            <CarRepairOutlined sx={{ color: theme.palette.primary.main }} />
          }
          title="Repair Cost Estimator"
        />
      }
      feedbackModal={
        <ChatFeedbackModal
          open={openFeedback}
          onClose={handleCloseFeedback}
          thumbsUp={thumbsUp}
          responseItemId={currentResponseId}
          createFeedback={createFeedback}
          updateFeedback={updateFeedback}
          hasRecord={
            typeof feedback[currentResponseId]?.thumbs_up === 'boolean'
          }
          feedbackText={
            feedback[currentResponseId]?.feedback?.feedback_text || ''
          }
        />
      }
      chatInput={
        <ChatAIInput
          isLoading={
            loadingStatus.message !== CHATAI_LOADING_STATUS.IDLE.message
          }
          disabled={messages.length === 0}
        />
      }
    >
      <MessagesContainer
        messages={messages}
        loadingStatus={loadingStatus}
        onSendMessage={sendMessage}
        onNewEstimate={handleNewEstimate}
        shouldResetInputs={shouldResetInputs}
        onResetComplete={handleResetComplete}
      />
    </FeaturePageShell>
  );
};
